import { Button, CIActionBtnGroup, CIInfoIcon, MyIcon } from 'ccs-ui-components';
import { useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from '../../../../../../store/AppStore';
import { getAssociatedIngressEgressEvents, getAssociatedIngressEvents } from '../../../../../../utilities/services/ServiceEventAssociationUtils';

import useConfirmDialog from '../../../../../../../common/hooks/ConfirmationDialog';

import classes from './EgressEventAssociationForm.module.scss';

interface IServiceEgressEventFormProps {
  serviceOperationName: string;
  ingressName: string;
  ingressEventVersion: string;
  isView: boolean;
  editEgressEventButtonClick: boolean;
  addEgressEvent: boolean;
  addEgressEventClicked: (ingressName: string, ingressEventVersion: string) => void;
  editEgressEvent: (eventId: string, eventVersion: string, ingressName: string, ingressEventVersion: string,) => void;
  deleteEgressEvent: (serviceOperationName: string, eventName: string, ingressName: string, eventVersion: string, ingressEventVersion: string) => void;
}

const EgressEventForm = ({
  serviceOperationName, isView, ingressName, ingressEventVersion, editEgressEventButtonClick, addEgressEvent,
  addEgressEventClicked, editEgressEvent, deleteEgressEvent
}: IServiceEgressEventFormProps) => {
  const { t: translate } = useTranslation();

  const useConfirmDialogHook = useConfirmDialog();
  const { egressEventMappingList } = useSelector(
    (state) => state.egressEventMappingReducer
  );

  const { ingressEventMappingList } = useSelector((state) => state.ingressEventMappingReducer);

  /**
   * Returns the associated egress event for service operation
   * @returns event list
   */
  const getAssociatedEgressEvent = () => {
    const eventList = egressEventMappingList.filter(
      (event) => event.serviceOperationName === serviceOperationName && event.ingressName === ingressName && event.ingressEventVersion === ingressEventVersion
    );
    return eventList;
  };

  /**
   * @description Method will prompt the dialog box to get the confirmation from user before delete the event.
   * @param operationName string
   * @param egressEvent string
   * @param ingressEvent string
   * @param egressVersion string
   * @param ingressVersion string
   */
  const confirmDeleteEgressEvent = (operationName: string, egressEvent: string, ingressEvent: string, egressVersion: string, ingressVersion: string) => {
    useConfirmDialogHook.show({ message: translate('delete-egress-event-association.message'), title: translate('confirm-dialog-prompt-box.title'), cancelText: translate('confirm-dialog-prompt-box.lbl_close'), okText: translate('confirm-dialog-prompt-box.lbl_confirm') }).then((confirmStatus) => {
      if (confirmStatus) {
        deleteEgressEvent(operationName, egressEvent, ingressEvent, egressVersion, ingressVersion);
      }
    });
  };

  const EgressEventAssociationList = () =>
    useMemo(
      () => (
        <div className={classes['ingress-event-list']}>
          <div className={classes['list-header']}>
            {translate('service_event_association.lbl_egress_event_tbl')}
          </div>
          {getAssociatedEgressEvent().length < 1 && (
            <Row className={classes['no-records']}>
              {translate('service_event_association.lbl_egress_no_records')}
            </Row>
          )}
          {getAssociatedEgressEvent().length > 0
            && getAssociatedEgressEvent().map((item) => (
              <Row className={`${classes['no-records']} ${egressEventMappingList.length > 0 && classes['with-record']}`} key={item.eventName}>
                <Col sm={9}>
                  <div className={classes['event-name']}>
                    {item.eventName}
                    <div className={classes['version-txt']}>Ver {item.eventVersion}</div>
                  </div>
                </Col>
                <Col sm={3}>
                  {isView ? (
                    <div className={classes['view-btn']}>
                      <CIActionBtnGroup buttons={[{ label: 'view', disabled: !isView, key: 'viewIcon', onClick: async () => editEgressEvent(item.eventName, item.eventVersion, ingressName, ingressEventVersion), testId: 'egress-view-button' }]} />
                    </div>)
                    : (
                      <div className={classes['button-container']}>
                        <Button
                          variant="tertiary"
                          onClick={() => editEgressEvent(item.eventName, item.eventVersion, ingressName, ingressEventVersion)}
                          data-testid="egress-edit-button"
                          disabled={isView || addEgressEvent}
                          className={classes['edit-button']}
                        >
                          <MyIcon name="editIcon" className={classes['icons-hover']} />
                        </Button>

                        <Button
                          variant="tertiary"
                          data-testid="egress-delete-button"
                          className={classes['delete-button']}
                          onClick={() => confirmDeleteEgressEvent(item.serviceOperationName, item.eventName, ingressName, item.eventVersion, ingressEventVersion)}
                          disabled={isView}
                        >
                          <MyIcon name="deleteIcon" className={classes['icons-hover']} />
                        </Button>

                      </div>)}
                </Col>
              </Row>
            ))}
        </div>
      ),
      []
    );

  return (
    <div className={classes.container}>
      <div className={classes['form-header-wrapper']}>
        <div className={classes['form-header']}>
          <span className={classes['header-title']}>
            {translate('service_event_association.lbl_egress_event')}
            <span>
              {` (${getAssociatedIngressEgressEvents(
                egressEventMappingList,
                serviceOperationName,
                ingressName,
                ingressEventVersion
              ).length})`}
            </span>
            <CIInfoIcon iconTxt={String(translate('service_event_form.desc_egress_event'))} />
          </span>
          <Button
            variant="primary"
            data-testid="egress-add-button"
            className={classes['add-button']}
            onClick={() => addEgressEventClicked(ingressName, ingressEventVersion)}
            disabled={isView || getAssociatedIngressEvents(ingressEventMappingList, serviceOperationName).length < 1}
          >
            {translate('service_event_association.lbl_add_egress_event')}
          </Button>
        </div>
      </div>
      {!editEgressEventButtonClick && <EgressEventAssociationList />}
      {useConfirmDialogHook.render()}
    </div>
  );
};

export default EgressEventForm;
